import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { CompanyMspInProductTrialsTabWebComponent } from '../component/CompanyMspInProductTrialsTabWebComponent';
import { CompanyMspInProductTrialsTabView } from '../view/CompanyMspInProductTrialsTabView';
import { templateCompanyMspInProductTrialsTab } from '../view/template';
import { CompanyFeatures, MspInProductTrials } from '../../../types/company-data';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { LandingPageEventsName } from '../../../../productTrials/common/gzLandingPage/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzLandingPageButtonId } from '../../../../productTrials/common/gzLandingPage/view/gzLandingPageConfig/gz-landing-page-button-id';
import {
  GzLandingPageDemoLinks,
  GzLandingPageProductLinks,
  GzLandingPageReportLinks,
  MspInProductTrialActionButtonId,
  MspInProductTrialEvents,
  MspInProductTrialId,
  translateProductIdReference,
  StartActionResponse,
  StopActionResponse,
  ReconfigureEndpointsResponse,
  getMspIptGenericTexts,
} from '../types/msp-in-product-trials-types';
import { translateFn } from '../../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { showToast } from '../../../../accounts/helpers/showToast';
import { ToastActionButtonMode, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { FeatureFlag, Rights } from '@nirvana/shared/constants';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { GzInProductTrialsVideoWebComponent } from '../../../../productTrials/common/gzTrialVideo';
import { MspInProductTrialsStartTrialPopUpWebComponent } from '../../../../productTrials/msp/mspInProductTrialsStartTrialPopUp';
import { FormGroupValue } from '../../../../ui/gzForm/formGroup/types/form-group-value';
import { WEB_COMPONENT_IN_PRODUCT_TRIALS_VIDEO } from '../../../../webcomponents.constants';
import { OnCompanyPanelClose } from '../../events/CompanyPanelEvent';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { downloadBlob } from '../../../../utils/files.utils';
import { getRequestDefaultOptions } from '../../../../utils/request.utils';
import { GetEdrTrialReportPdfRequest } from '@nirvana/shared/dtos/types/dashboards/common';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';

export class CompanyMspInProductTrialsTabController extends BaseWebComponentController {
  public component: CompanyMspInProductTrialsTabWebComponent;

  public view: CompanyMspInProductTrialsTabView;

  constructor(component: CompanyMspInProductTrialsTabWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompanyMspInProductTrialsTabView,
      viewTemplate: templateCompanyMspInProductTrialsTab,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$gzLandingPage,
        event: LandingPageEventsName.PRODUCT_ITEM,
        callback: async (
          event: CustomEvent<{
            target: GzButtonWebComponent;
            type: MspInProductTrialId;
          }>
        ) => {
          await this.handleGzLandingPageButtonClicked(event.detail.target, event.detail.type);
        },
      },
      {
        element: this.view.dom.$gzLandingPage,
        event: LandingPageEventsName.SHOW_CARDS,
        callback: this.showCards,
      },
      {
        element: this.view.dom.$gzLandingPage,
        event: LandingPageEventsName.MSP_TRIAL_ACTION,
        callback: async (
          event: CustomEvent<{
            target: GzButtonWebComponent
            productId: MspInProductTrialId;
          }>
        ) => {
          await this.handleGzLandingPageMspActionButtonClicked(event.detail.target, event.detail.productId);
        },
      },
    ];
  }

  public setTrialFeatures(companyFeatures: CompanyFeatures, mspInProductTrials: MspInProductTrials | undefined): void {
    this.view.resetTrialCards();

    if (!mspInProductTrials || isEmpty(mspInProductTrials)) {
      return;
    }

    const edrIsInTrial = !!(
      mspInProductTrials.edr.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE] ||
      mspInProductTrials.mdr.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE]
    );
    this.view.isEdrPaid = (companyFeatures.edrUsable && !edrIsInTrial) ?? false;

    if (mspInProductTrials.mdr.visible) {
      this.view.addTrialCard(MspInProductTrialId.MdrTrial, mspInProductTrials.mdr);
    }

    if (mspInProductTrials.edr.visible) {
      this.view.addTrialCard(MspInProductTrialId.EdrTrial, mspInProductTrials.edr);
    }
  }

  public setCompanySelectedCountry(companySelectedCountry: string): void {
    this.view.companySelectedCountry = companySelectedCountry;
  }

  public setIndustrySelectedIndustry(companySelectedIndustry: number): void {
    this.view.companySelectedIndustry = companySelectedIndustry;
  }

  public setCompanySelectedState(companySelectedState: string): void {
    this.view.companySelectedState = companySelectedState;
  }

  private handleGzLandingPageButtonClicked(target: GzButtonWebComponent, productId: MspInProductTrialId): void {
    switch (target.id) {
      case GzLandingPageButtonId.FreeTrial:
        this.showStartTrialConfirmationWindow(productId);
        return;
      case GzLandingPageButtonId.StopTrial:
        this.showStopTrialConfirmationWindow(productId);
        return;
      case GzLandingPageButtonId.HowTo:
        this.openHowToLinkInProductTrial(productId);
        return;
      case GzLandingPageButtonId.ViewDemo:
        this.showDemoForInProductTrial(productId);
        return;
    }
  }

  private handleGzLandingPageMspActionButtonClicked(target: GzButtonWebComponent, productId: MspInProductTrialId): void {
    switch (target.id) {
      case MspInProductTrialActionButtonId.Reconfigure:
        this.showReconfigureConfirmationWindow(productId);
        return;
      case MspInProductTrialActionButtonId.CreateReport:
        this.handleCreateReportButtonClicked(productId);
        return;
      default:
        return;
    }
  }

  private showReconfigureConfirmationWindow(
    productTrialId: MspInProductTrialId
  ): void {
    const confirmationWindowItem = this.view.reconfigureConfirmationWindow;
    const windowBodyHtml = `
      <p>
        ${confirmationWindowItem.bodyText}
      </p>
    `;

    const windowFooterHtml = `<gz-button
                                id='confirm'
                                type=${confirmationWindowItem.isAddModulesAction ? 'full' : 'outline'}
                                color=${confirmationWindowItem.isAddModulesAction ? 'primary' : 'negative'}
                                class='mr-2'>
       ${confirmationWindowItem.confirmButtonText}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${confirmationWindowItem.cancelButtonText}
      </gz-button>`;

    const windowTitle = confirmationWindowItem.titleText;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        try {
          const response = await ConsoleRequest.createRequest<any>({
            dataProviderType: DataProviderType.CORE,
            endpoint: 'MspInProductTrials',
            method: 'reconfigureTask',
            payload: {
              companyId: this.component.companyId,
              productTrialId: productTrialId,
              isAddModulesAction: confirmationWindowItem.isAddModulesAction
            },
          });
          if (response === ReconfigureEndpointsResponse.SuccessfulReconfigureInitiation) {
            showToast(ToastSeverity.SUCCESS, confirmationWindowItem.toastSuccessText);
          }
          if (response === ReconfigureEndpointsResponse.SkippedReconfigureInitiation) {
            showToast(ToastSeverity.INFORMATIVE, confirmationWindowItem.toastInfoText);
          }
        } catch (error) {
          showToast(ToastSeverity.ERROR, confirmationWindowItem.toastErrorText);
        } finally {
          dialog.close();
        }
      },
      null,
      false
    );
  }

  private handleCreateReportButtonClicked(productId: MspInProductTrialId): void {
    if (productId === MspInProductTrialId.MdrTrial) {
      window.open(GzLandingPageReportLinks[productId], '_blank');
    }
    if (productId === MspInProductTrialId.EdrTrial) {
      this.exportEdrTrialPdf();
    }
  }

  private async exportEdrTrialPdf(): Promise<void> {

    const exportButton = this.view.dom.$gzLandingPage.shadowRoot?.querySelector('#' + MspInProductTrialActionButtonId.CreateReport) as GzButtonWebComponent;
    exportButton.showSpinner = true;
    exportButton.disabled = true;

    try {
      const reportData = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'MspInProductTrials',
        method: 'getMspInProductTrialsEdrReportData',
        payload: {
          companyId: this.component.companyId,
        },
      });

      const params: GetEdrTrialReportPdfRequest = {
        data: reportData,
        timezone: 'UTC',
        locale: 'en_US',
      };

      const response = await this.dependencies.nirvanaDependencies.api.clients.Dashboards.getEdrTrialReportPdf(
        params,
        getRequestDefaultOptions(),
      ).toPromise();

      const fileBlob = new Blob([response.pdf], {type: 'application/pdf'});
      const reportTime = getFormattedDateTime(
        TimestampFormat.FILES,
        Date.now(),
        this.dependencies.getTimezone()
      );
      const edrTrialReportPdfName = `${reportData.companyName}_EDR_Trial_report_${reportTime}.pdf`;
      downloadBlob(fileBlob, edrTrialReportPdfName);
    } catch (error) {
      showToast(ToastSeverity.ERROR, translateFn('Failed to generate report. If the issue persists, please contact Support.'));
    } finally {
      exportButton.showSpinner = false;
      exportButton.disabled = false;
    }
  }

  private showCards(): void {
    this.view.removeElementClass(this.view.dom.$trialCardsSection, 'hidden');
    this.view.setElementClass(this.view.dom.$gzLandingPage, 'hidden');
  }

  public showStartTrialConfirmationWindow(trialId: MspInProductTrialId): void {
    this.view.trackPage(translateProductIdReference(trialId) + ':start trial confirmation window');

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
       <gz-button id='confirm' type='full' color='primary' class='mr-2'>
       ${translateFn('Start trial')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const mspInProductTrialsStartTrialPopUpWebComponent = new MspInProductTrialsStartTrialPopUpWebComponent();
    const dialogConfig = {
      headline: translateFn('Start trial?'),
      bodyContent: mspInProductTrialsStartTrialPopUpWebComponent,
      footerContent: dialogFooter,
      isOpen: true
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfig, document.querySelector('.mainContainer') as HTMLElement
    );

    const footerConfirmBtn = dialogFooter.querySelector('#confirm') as GzButtonWebComponent;
    if (footerConfirmBtn) {
      footerConfirmBtn.addEventListener('click', async () => {
        this.view.trackPage(translateProductIdReference(trialId) + ':confirm start trial');

        const formGroup = mspInProductTrialsStartTrialPopUpWebComponent.getFormGroup();
        formGroup.validate(true);
        if (!formGroup.valid) {
          return;
        }
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        try {
          const response = await ConsoleRequest.createRequest<any>({
            dataProviderType: DataProviderType.CORE,
            endpoint: 'MspInProductTrials',
            method: 'startTrial',
            payload: {
              companyId: this.component.companyId,
              productTrialId: trialId,
              companyCountry: formGroup.value.country,
              companyIndustry: formGroup.value.industry,
              companyState: formGroup.value.state
            },
          });
          showToast(ToastSeverity.SUCCESS, translateFn('Trial successfully started.'));
          this.handleResponseForStartTrial(response as StartActionResponse);
        } catch (error) {
          showToast(ToastSeverity.ERROR, translateFn('Failed to start trial.'));
        } finally {
          dialog.close();
        }
      });
    }

    const footerCancelBtn = dialogFooter.querySelector('#cancel');
    if (footerCancelBtn) {
      footerCancelBtn.addEventListener('click', () => {
        dialog.close();
      });
    }

    mspInProductTrialsStartTrialPopUpWebComponent.injectDependencies(this.dependencies);
    mspInProductTrialsStartTrialPopUpWebComponent.setContext(trialId);
    mspInProductTrialsStartTrialPopUpWebComponent
      .start()
      .then(() => {
          mspInProductTrialsStartTrialPopUpWebComponent.setFormGroupValues({
            country: this.view.companySelectedCountry,
            industry: String(this.view.companySelectedIndustry),
            state: this.view.companySelectedState,
          } as FormGroupValue);
          mspInProductTrialsStartTrialPopUpWebComponent.getFormGroup().valid$.subscribe({
            next: valid => {
              footerConfirmBtn.disabled = !valid;
            },
          })
        }
      )
      .catch();
  }

  private handleResponseForStartTrial(response: StartActionResponse) {
    this.component.dispatchEvent(
      new CustomEvent(MspInProductTrialEvents.TrialStatusModified, {
        bubbles: true,
        cancelable: true,
        composed: false,
      }),
    );

    switch (response) {
      case StartActionResponse.WithSuccessfulReconfigureTaskInitiation:
        showToast(ToastSeverity.SUCCESS, getMspIptGenericTexts().reconfigureSuccessToastText);
        break;
      case StartActionResponse.WithFailedReconfigureTaskInitiation:
        showToast(ToastSeverity.ERROR, getMspIptGenericTexts().reconfigureErrorToastText);
        break;
      default:
        break;
    }
  }

  private handleResponseForStopTrial(response: StopActionResponse) {
    this.component.dispatchEvent(
      new CustomEvent(MspInProductTrialEvents.TrialStatusModified, {
        bubbles: true,
        cancelable: true,
        composed: false,
      }),
    );

    switch (response) {
      case StopActionResponse.WithSuccessfulReconfigureTaskInitiation:
        showToast(ToastSeverity.SUCCESS, getMspIptGenericTexts().reconfigureSuccessToastText);
        break;
      case StopActionResponse.WithFailedReconfigureTaskInitiation:
        showToast(ToastSeverity.ERROR, getMspIptGenericTexts().reconfigureErrorToastText);
        break;
      case StopActionResponse.WithoutReconfigureTask:
        break;
      default:
        break;
    }
  }

  public showStopTrialConfirmationWindow(trialId: MspInProductTrialId): void {
    this.view.trackPage(translateProductIdReference(trialId) + ':stop trial confirmation window');

    let windowBodyHtml = `
      <p>
        ${translateFn(
          'You will not be able to enroll in this product trial again for 6 months. The service and associated add-ons will be disabled for the target company and the service will no longer be available.'
        )}
      </p>
    `;
    if (!this.view.isEdrPaid && Application.Main.hasAllRights([Rights.RIGHT_MANAGE_INVENTORY])) {
      windowBodyHtml += `
        <gz-checkbox gz-form-input id='reconfigure-task-checkbox' class='mt-2' label=''}>
          ${translateFn('Remove module from all endpoints')}
        </gz-checkbox>
      `;
    }

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translateFn('End trial')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Stop trial?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        this.view.trackPage(translateProductIdReference(trialId) + ':confirm stop trial');

        const reconfigureTaskCheckbox = dialog.shadowRoot?.querySelector<GzCheckboxWebComponent>('#reconfigure-task-checkbox');
        const createReconfigureTask = (reconfigureTaskCheckbox && reconfigureTaskCheckbox.checked) ?? false;
        try {
          const response = await ConsoleRequest.createRequest<any>({
            dataProviderType: DataProviderType.CORE,
            endpoint: 'MspInProductTrials',
            method: 'stopTrial',
            payload: {
              companyId: this.component.companyId,
              productTrialId: trialId,
              createReconfigureTask: createReconfigureTask,
            },
          });
          showToast(ToastSeverity.SUCCESS, translateFn('Trial ended.'));
          this.handleResponseForStopTrial(response as StopActionResponse);
        } catch (error) {
          showToast(ToastSeverity.ERROR, translateFn('Failed to stop trial.'));
        } finally {
          dialog.close()
        }
      },
      null,
      false
    );
  }

  private openHowToLinkInProductTrial(productId: MspInProductTrialId) {
    const pageLink = GzLandingPageProductLinks[productId];
    const link = pageLink.link + `?icid=button|b|${pageLink.id}|how-to`;
    window.open(link, '_blank');
  }

  public isVideoPlaying(): boolean {
    const videoComponent = document.body.getElementsByTagName(WEB_COMPONENT_IN_PRODUCT_TRIALS_VIDEO)[0] as GzInProductTrialsVideoWebComponent;
    if (videoComponent) {
      videoComponent.close();
      return true;
    }
    return false;
  }

  private showDemoForInProductTrial(productId: MspInProductTrialId) {
    const link = GzLandingPageDemoLinks[productId];
    if (!link) {
      return;
    }

    analytics.trackPageInteraction({
      reference: translateProductIdReference(productId),
      asset: 'view-demo',
    });

    GzInProductTrialsVideoWebComponent.computeVideoComponent(link);
  }
}
